// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

export function PromotionsPanel({ seller }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const plusWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    code: "",
    type: "percentage",
    value: "",
    min_order_amount: "",
    max_uses: "",
    starts_at: today,
    ends_at: plusWeek,
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  async function load() {
    if (!seller?.id) return;
    const { data } = await supabase
      .from("promotions")
      .select("id, code, type, value, min_order_amount, max_uses, current_uses, starts_at, ends_at, status")
      .eq("seller_id", seller.id)
      .order("created_at", { ascending: false });
    setRows(data || []);
    setLoading(false);
  }

  useEffect(() => { load(); }, [seller?.id]);

  async function create() {
    const code = form.code.trim().toUpperCase();
    const value = parseInt(form.value, 10);
    if (!code) { setMsg({ tone: "red", text: "Promo code is required." }); return; }
    if (!value || value <= 0) { setMsg({ tone: "red", text: "Enter a discount value > 0." }); return; }
    if (form.type === "percentage" && value > 100) { setMsg({ tone: "red", text: "Percentage cannot exceed 100." }); return; }
    if (new Date(form.ends_at) < new Date(form.starts_at)) { setMsg({ tone: "red", text: "End date must be after start date." }); return; }

    setSubmitting(true);
    setMsg(null);
    // `value` is stored as-is for percentage (0-100). For fixed, input is in
    // Kwacha so convert to ngwee. Same convention as mobile BPromotions.
    const storedValue = form.type === "percentage" ? value : Math.round(value * 100);
    const minOrder = form.min_order_amount ? Math.round(parseFloat(form.min_order_amount) * 100) : 0;
    const { error } = await supabase.from("promotions").insert({
      seller_id: seller.id,
      code,
      type: form.type,
      value: storedValue,
      min_order_amount: minOrder,
      max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      starts_at: form.starts_at,
      ends_at: form.ends_at,
      status: new Date(form.starts_at) > new Date() ? "scheduled" : "active",
    });
    setSubmitting(false);
    if (error) {
      setMsg({ tone: "red", text: error.message });
      return;
    }
    setMsg({ tone: "green", text: `Promo ${code} created.` });
    setForm({ code: "", type: "percentage", value: "", min_order_amount: "", max_uses: "", starts_at: today, ends_at: plusWeek });
    setCreating(false);
    load();
  }

  async function toggleStatus(p) {
    const next = p.status === "active" ? "paused" : "active";
    const { error } = await supabase.from("promotions").update({ status: next }).eq("id", p.id);
    if (!error) setRows((prev) => prev.map((r) => (r.id === p.id ? { ...r, status: next } : r)));
  }

  async function deletePromo(id) {
    if (!confirm("Delete this promotion? This cannot be undone.")) return;
    const { error } = await supabase.from("promotions").delete().eq("id", id);
    if (!error) setRows((prev) => prev.filter((r) => r.id !== id));
  }

  if (!seller) return <EmptyState icon="tag" title="No store linked" body="Switch to a seller account." />;

  const statusTint = { active: C.greenDk, paused: C.muted, expired: C.red, scheduled: C.hunter };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {!creating && (
        <Btn onClick={() => { setCreating(true); setMsg(null); }} icon="plus">Create promo code</Btn>
      )}

      {creating && (
        <Card pad={22}>
          <Eyebrow>New promotion</Eyebrow>
          <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Promo code">
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="BACK2CAMPUS"
                  style={{ ...inputStyle, textTransform: "uppercase", letterSpacing: 1 }}
                />
              </Field>
              <Field label="Discount type">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} style={inputStyle}>
                  <option value="percentage">Percentage off</option>
                  <option value="fixed">Fixed amount off (Kwacha)</option>
                </select>
              </Field>
            </div>
            <Field label={form.type === "percentage" ? "Discount %" : "Discount amount (Kwacha)"}>
              <input
                type="number"
                min="0"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder={form.type === "percentage" ? "e.g. 15" : "e.g. 50"}
                style={inputStyle}
              />
            </Field>
            <Field label="Minimum order amount — optional (Kwacha)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.min_order_amount}
                onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                placeholder="Leave blank for no minimum"
                style={inputStyle}
              />
            </Field>
            <Field label="Usage limit — optional">
              <input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                placeholder="Blank = unlimited"
                style={inputStyle}
              />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Starts on">
                <input type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} style={inputStyle} />
              </Field>
              <Field label="Ends on">
                <input type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} style={inputStyle} />
              </Field>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Btn onClick={create} disabled={submitting}>{submitting ? "Creating…" : "Create promotion"}</Btn>
              <Btn kind="outline" size="sm" onClick={() => { setCreating(false); setMsg(null); }}>Cancel</Btn>
              {msg && <span style={{ fontSize: 12, fontWeight: 700, color: msg.tone === "red" ? C.red : C.greenDk }}>{msg.text}</span>}
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <Skeleton h={80} />
      ) : rows.length === 0 ? (
        !creating && <EmptyState icon="tag" title="No promotions yet" body="Create your first promo code to drive sales." />
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((p) => {
            const value = p.type === "percentage" ? `${p.value}% off` : `${fmtK(p.value)} off`;
            return (
              <Card key={p.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 40, height: 40, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="tag" size={16} color={C.hunter} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.charcoal }}>
                      {p.code} <span style={{ fontWeight: 600, color: C.slate }}>· {value}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {p.current_uses || 0}/{p.max_uses ?? "∞"} uses · {new Date(p.starts_at).toLocaleDateString()} → {new Date(p.ends_at).toLocaleDateString()}
                      {p.min_order_amount > 0 && ` · min ${fmtK(p.min_order_amount)}`}
                    </div>
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px", borderRadius: 6, background: C.cream, color: statusTint[p.status] || C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                    {p.status}
                  </span>
                  {(p.status === "active" || p.status === "paused") && (
                    <Btn size="sm" kind="outline" onClick={() => toggleStatus(p)}>
                      {p.status === "active" ? "Pause" : "Resume"}
                    </Btn>
                  )}
                  <button onClick={() => deletePromo(p.id)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}>
                    <Icon name="trash-2" size={14} color={C.red} />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

